"use client"
import { CaretDownIcon } from "@radix-ui/react-icons"
import type React from "react"

import { hasChainIcon } from "@src/app/(home)/_utils/useDeterminePair"
import { useIsFlatTokenListEnabled } from "@src/hooks/useIsFlatTokenListEnabled"
import { useMemo } from "react"
import { chainIcons } from "../constants/blockchains"
import type { TokenInfo } from "../types/base"
import { isBaseToken } from "../utils"
import { AssetComboIcon } from "./Asset/AssetComboIcon"

type Props = {
  selected?: TokenInfo
  handleSelect?: () => void
  tokens?: TokenInfo[]
  tokenIn?: TokenInfo
  tokenOut?: TokenInfo
}

const EmptyIcon = () => {
  return (
    <span className="relative min-w-[36px] min-h-[36px] bg-gray-200 rounded-full" />
  )
}

export const SelectAssets = ({
  selected,
  handleSelect,
  tokens,
  tokenIn,
  tokenOut,
}: Props) => {
  const handleAssetsSelect = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleSelect?.()
  }

  const chainIcon = useMemo(() => {
    return selected !== undefined && isBaseToken(selected)
      ? chainIcons[selected.originChainName]
      : undefined
  }, [selected])

  const isFlatTokenListEnabled = useIsFlatTokenListEnabled()

  const showChainIcon = useMemo(() => {
    if (isFlatTokenListEnabled && chainIcon !== undefined) {
      return true
    }

    if (
      chainIcon === undefined ||
      tokens === undefined ||
      selected === undefined
    ) {
      return false
    }

    const allTokens = [...tokens]

    if (tokenIn !== undefined) {
      allTokens.push(tokenIn)
    }

    if (tokenOut !== undefined) {
      allTokens.push(tokenOut)
    }

    return hasChainIcon(selected, allTokens)
  }, [tokens, tokenIn, tokenOut, selected, chainIcon, isFlatTokenListEnabled])

  return (
    <button
      type="button"
      onClick={handleAssetsSelect}
      className="max-w-[148px] md:max-w-[210px] bg-[#1F1F1F] hover:bg-black flex justify-between items-center min-h-[40px] px-2.5 gap-2.5 transition-colors"
      style={{
        clipPath: "polygon(0 0, 100% 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))"
      }}
    >
      {selected?.icon ? (
        <AssetComboIcon
          icon={selected.icon as string}
          name={selected.name as string}
          chainName={
            isBaseToken(selected) ? selected.originChainName : undefined
          }
          chainIcon={chainIcon}
          showChainIcon={showChainIcon}
        />
      ) : (
        <EmptyIcon />
      )}
      <span className="text-sm font-medium uppercase truncate">
        {selected?.symbol ?? "select token"}
      </span>
      <CaretDownIcon width={8} height={8} className="ml-2.5" />
    </button>
  )
}
